import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store/redux/store/store';
import { Roles } from '@/models/general';
import { Trophy } from 'lucide-react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { DateField } from '@/components/dateFields';
import Select from '../../../components/ui/Select';
import Pagination from '@/components/Pagination';
import { PageLayout } from '../../../components/ui/PageLayout';
import { LoadingState } from '../../../components/AsyncStates';
import { cn } from '@/utils/cn';
import TournamentCard from './components/TournamentCard';
import AddTournamentModal from './components/AddTournamentModal';
import EditTournamentModal from './components/EditTournamentModal';
import DeleteTournamentModal from './components/DeleteTournamentModal';
import ArchiveTournamentModal from './components/ArchiveTournamentModal';
import { useTournaments } from '@/hooks/useTournaments';
import { useTournamentStats } from '@/hooks/useTournamentStats';
import { TournamentStatus } from '@/models/tournament';
import type { TournamentView, TournamentListQuery } from '@/models/tournament';

const ARCHIVED_FILTER = 'archived';

const TournamentList: React.FC = () => {
  const { t } = useTranslation('tournament');
  const navigate = useNavigate();
  const canManage = useSelector((state: RootState) => !state.user.firstLoad &&
    (state.user.role === Roles.Hr || state.user.role === Roles.Admin));
  // CO-922 (owner decisions 2026-10-21): permanent cascade delete and the Archived list are HR-only
  // (HR_ONLY_ROLES); the server answers 403 to anyone else on both, so the UI never offers them.
  const isHr = useSelector((state: RootState) => !state.user.firstLoad && state.user.role === Roles.Hr);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<TournamentView | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  // Build query for API
  // 'archived' is not a TournamentStatus: it switches the list to archived rows (HR only, CO-922).
  const showArchived = isHr && statusFilter === ARCHIVED_FILTER;
  const query: TournamentListQuery = useMemo(() => ({
    search: searchTerm || undefined,
    status: statusFilter !== '' && statusFilter !== ARCHIVED_FILTER ? (parseInt(statusFilter) as TournamentStatus) : undefined,
    archived: showArchived || undefined,
    startDateFrom: dateRange.start || undefined,
    startDateTo: dateRange.end || undefined,
    page: currentPage,
    pageSize: itemsPerPage,
  }), [searchTerm, statusFilter, showArchived, dateRange.start, dateRange.end, currentPage, itemsPerPage]);

  const { data, loading, error, refetch } = useTournaments(query);
  const { data: stats } = useTournamentStats();

  // Paging and filtering re-run the query, so `loading` goes true on EVERY change. Gating the grid
  // on it tore the cards out and dropped a centred spinner in their place - the page collapsed to
  // spinner height and snapped back on every next-page click. Only the very first load (nothing to
  // show yet) gets the spinner; later fetches keep the current cards on screen and just dim them.
  const isInitialLoad = loading && !data;
  const isRefetching = loading && !!data;

  const tournaments = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 0;

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateRange.start, dateRange.end]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewTournament = (tournament: TournamentView) => {
    navigate(`/tournament-management/matches?tournamentId=${tournament.id}`);
  };

  const handleEditTournament = (tournament: TournamentView) => {
    if (!canManage) return;
    setSelectedTournament(tournament);
    setShowEditModal(true);
  };

  const handleArchiveTournament = (tournament: TournamentView) => {
    if (!canManage) return;
    setSelectedTournament(tournament);
    setShowArchiveModal(true);
  };

  const handleDeleteTournament = (tournament: TournamentView) => {
    if (!isHr) return;
    setSelectedTournament(tournament);
    setShowDeleteModal(true);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setDateRange({ start: '', end: '' });
  };

  const hasActiveFilters = searchTerm || statusFilter || dateRange.start || dateRange.end;


  return (
    <PageLayout
      title={t('Tournaments')}
      description={t('Manage tournaments, matches, and official assignments')}
      icon={Trophy}
      accentColor="amber"
      maxWidth="1600px"
      headerActions={
        canManage ? <Button
          variant="outline"
          onClick={() => setShowAddModal(true)}
          iconName="Plus"
          data-testid="tournament-add-btn"
        >
          {t('Add Tournament')}
        </Button> : undefined
      }
    >
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            {[
              { label: t('Total'), value: stats.totalTournaments, icon: 'Trophy' },
              { label: t('Active'), value: stats.activeTournaments, icon: 'Play' },
              { label: t('Upcoming'), value: stats.upcomingTournaments, icon: 'Calendar' },
              { label: t('Completed'), value: stats.completedTournaments, icon: 'CheckCircle' },
              { label: t('Matches'), value: stats.totalMatches, icon: 'Swords' },
            ].map((card) => (
              <div key={card.label} className="bg-card rounded-lg border border-border p-4 flex flex-col items-center justify-center text-center" data-testid={`stat-card-${card.label.toLowerCase()}`}>
                <span className="text-2xl font-bold text-gray-900">{card.value}</span>
                <span className="text-sm text-muted-foreground mt-1">{card.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6" data-testid="tournament-filters">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Icon name="Filter" size={20} className="me-2" />
              {t('Filters')}
            </h2>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900"
                data-testid="tournament-clear-filters-btn"
              >
                <Icon name="X" size={14} className="me-1" />
                {t('Clear All')}
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <Input
                type="text"
                placeholder={t('Search tournaments, venues...')}
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                className="w-full"
                data-testid="tournament-search-input"
              />
            </div>

            {/* Status Filter */}
            <div>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: '', label: t('All Statuses') },
                  { value: '0', label: t('Upcoming') },
                  { value: '1', label: t('Active') },
                  { value: '2', label: t('Completed') },
                  { value: '3', label: t('Cancelled') },
                  ...(isHr ? [{ value: ARCHIVED_FILTER, label: t('archiveTournament.filterOption') }] : []),
                ]}
                placeholder={t('Status')}
                data-testid="tournament-status-filter"
              />
            </div>

            {/* Date Range - a 2-col grid, not a flex row with a dash. Chrome's date input has an
                intrinsic min-width around 120px and will not shrink below it, so two of them plus a
                separator in one un-minned flex row overflowed the filter card at 320px. `min-w-0`
                on each cell lets them actually shrink. `placeholder` does nothing on a date input,
                so the accessible name comes from aria-label. */}
            <div className="grid grid-cols-2 gap-2">
              <div className="min-w-0">
                <DateField
                  value={dateRange.start}
                  onChange={(v) => setDateRange(prev => ({ ...prev, start: v }))}
                  aria-label={t('Start Date')}
                  data-testid="tournament-date-start"
                />
              </div>
              <div className="min-w-0">
                <DateField
                  value={dateRange.end}
                  onChange={(v) => setDateRange(prev => ({ ...prev, end: v }))}
                  aria-label={t('End Date')}
                  data-testid="tournament-date-end"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Loading State - first load only; see isInitialLoad above. */}
        {isInitialLoad && (
          <div className="py-12">
            <LoadingState title={t('Loading tournaments...')} size="md" />
          </div>
        )}

        {/* Error State */}
        {error && !isInitialLoad && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <div className="flex items-start">
              <Icon name="AlertCircle" size={20} className="text-red-600 me-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-red-900 mb-1">{t('Error loading tournaments')}</h3>
                <p className="text-sm text-red-700">{error}</p>
                <Button variant="outline" size="sm" onClick={refetch} className="mt-3" data-testid="tournament-retry-btn">
                  {t('Try Again')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {!isInitialLoad && !error && (
          <>
            {/* Tournament Grid */}
            {tournaments.length === 0 ? (
              // Two different situations were wearing one message: a brand-new tenant with zero
              // tournaments was told to "adjust your filters" it had never set, and was offered no
              // way to create the first one. Branch on whether filters are actually active.
              !hasActiveFilters ? (
                <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center" data-testid="tournament-empty-no-data">
                  <Icon name="Trophy" size={48} className="mx-auto text-muted-foreground/40 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('emptyState.noTournamentsYet')}</h3>
                  {canManage && <p className="text-gray-600 mb-6 max-w-md mx-auto">{t('emptyState.noTournamentsYetDesc')}</p>}
                  {canManage && <Button
                    onClick={() => setShowAddModal(true)}
                    variant="warning"
                    iconName="Plus"
                    data-testid="empty-add-tournament-btn"
                  >
                    {t('Add Tournament')}
                  </Button>}
                </div>
              ) : (
                <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center" data-testid="tournament-empty-state">
                  <Icon name="SearchX" size={48} className="mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('No tournaments found')}</h3>
                  <p className="text-gray-600 mb-6">{t('Try adjusting your filters or search terms')}</p>
                  <Button onClick={clearFilters} variant="outline" iconName="X" data-testid="tournament-clear-filters-empty-btn">
                    {t('Clear Filters')}
                  </Button>
                </div>
              )
            ) : (
              <div
                className={cn(
                  'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6',
                  'transition-opacity duration-200 ease-out motion-reduce:transition-none',
                  isRefetching && 'opacity-60 pointer-events-none',
                )}
                aria-busy={isRefetching}
                data-testid="tournament-grid"
              >
                {tournaments.map(tournament => (
                  <TournamentCard
                    key={tournament.id}
                    tournament={tournament}
                    canManage={canManage}
                    canDeletePermanently={isHr}
                    onView={() => handleViewTournament(tournament)}
                    onEdit={() => handleEditTournament(tournament)}
                    onArchive={() => handleArchiveTournament(tournament)}
                    onDelete={() => handleDeleteTournament(tournament)}
                  />
                ))}
              </div>
            )}

            {/* Pagination Controls */}
            {totalCount > 0 && (
                <div
                  className={cn(
                    'transition-opacity duration-200 ease-out motion-reduce:transition-none',
                    isRefetching && 'opacity-60 pointer-events-none',
                  )}
                  aria-busy={isRefetching}
                >
                  <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      variant="full"
                      pageSize={itemsPerPage}
                      pageSizeOptions={[6, 12, 18, 24]}
                      onPageSizeChange={(size) => { setItemsPerPage(size); setCurrentPage(1); }}
                      totalCount={totalCount}
                  />
                </div>
            )}
          </>
        )}

      {/* Modals */}
      <AddTournamentModal
        isOpen={canManage && showAddModal}
        onClose={() => setShowAddModal(false)}
        onCreated={refetch}
      />
      <EditTournamentModal
        isOpen={canManage && showEditModal}
        onClose={() => setShowEditModal(false)}
        tournament={selectedTournament}
        onUpdated={refetch}
      />
      <ArchiveTournamentModal
        isOpen={canManage && showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        tournament={selectedTournament}
        onArchived={refetch}
      />
      <DeleteTournamentModal
        isOpen={isHr && showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        tournament={selectedTournament}
        onDeleted={refetch}
      />
    </PageLayout>
  );
};

export default TournamentList;
